import moment from 'moment-timezone';

import store from 'web-app/react/redux/store';
import generateUUID from 'web-app/util/uuid';
import ENV from 'web-app/config/environment';
import * as SentryService from 'web-app/services/sentry';
import LocalStorage from 'web-app/services/local-storage';
import {analyticsBehavior} from 'web-app/react/session/account/selectors';
import getInstallationId from 'web-app/services/installation-id-manager';
import PlatformHelper from 'web-app/helpers/platform-helper';
import {saveEventInLocalStorage} from 'web-app/react/tracking-visualization/helpers';
import {checkIfFlagIsToggled} from 'web-app/react/routes/debugger/local-storage-config';
import {findPathFromUrl} from 'web-app/react/routes/helpers';
import {extractPathFromHash} from 'web-app/util/url';

import {type Event, type DynamicTrackingEvent, type TrackingEvent, isValidTrackingEvent} from './types';
import * as Storage from './storage';
import {getMixpanelGroupAnalyticsProperties} from './group-analytics';
import {getPlanhatProperties} from './planhat';
import {triggerManuallyAndFlush} from './event-consumer';
import {isOnUkPackageTrial, isUkPackageTrialEvent} from './segmented-events';

/**
 *
 * @param event Event name
 * @param meta Event properties
 * @returns
 */
export const track = (event: DynamicTrackingEvent | string | TrackingEvent, meta?: object) => {
    const state = store.getState();
    const account = state.session.account;
    const loginId = account.loginId;
    const localSecret = account.localSecret;
    const behavior = analyticsBehavior(state);
    const isTouchDevice = PlatformHelper.isTouch();
    if (!loginId || !localSecret || !behavior) {
        return;
    }

    if (meta && Object.prototype.hasOwnProperty.call(meta, 'serverProvided')) {
        SentryService.captureMessage('Attempted tracking with a meta object with the key "serverProvided"', {});
    }

    const currentLocation = findPathFromUrl(extractPathFromHash(window.location.hash));
    const mixpanelGroupAnalyticsProperties = getMixpanelGroupAnalyticsProperties(behavior);
    const planhatProperties = getPlanhatProperties(
        loginId,
        mixpanelGroupAnalyticsProperties.Institution,
        mixpanelGroupAnalyticsProperties.Organisation,
    );

    const isLegacyEvent = typeof event === 'string';
    const isValidEvent = isValidTrackingEvent(event);
    const isNonLegacyAndInvalid = !isLegacyEvent && !isValidEvent;

    if (isNonLegacyAndInvalid) {
        return;
    }

    const eventName = isValidEvent ? event.name : event;
    const sendTo = isValidEvent ? event.sendTo : ['Mixpanel'];

    const internalEvent: Event = {
        eventId: generateUUID(),
        name: eventName,
        meta:
            {
                serverProvided: behavior.payload && behavior.payload.meta,
                location: currentLocation,
                isTouchDevice,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                windowWidth:
                    window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth || undefined,
                windowHeight:
                    window.innerHeight ||
                    document.documentElement.clientHeight ||
                    document.body.clientHeight ||
                    undefined,
                sendTo,
                ...mixpanelGroupAnalyticsProperties,
                ...meta,
                ...planhatProperties,
            } || {},
        loginId,
        deviceId: LocalStorage.getFromLocalStorage('deviceId'),
        installationId: getInstallationId(),
        timestamp: moment().toISOString(),
        appVersion: ENV.APP.version,
        commit: ENV.APP.commit,
    };

    if (isUkPackageTrialEvent(eventName) && !isOnUkPackageTrial(behavior)) {
        return;
    }

    if (checkIfFlagIsToggled('TRACKING_VISUALIZATION')) {
        saveEventInLocalStorage(internalEvent);
    }

    if (checkIfFlagIsToggled('FAMLYTICS')) {
        // eslint-disable-next-line no-console
        console.log(internalEvent.name, internalEvent);
        return;
    }

    // Don't save events in local storage for Mixpanel if we are on app.tryfamly.co
    // The feature ENABLE_ANALYTICS is globally enabled on tryfamly for testing purposes, so we want to avoid sending events to Mixpanel
    if (window.location.hostname === 'app.tryfamly.co') {
        return;
    }

    Storage.saveEvent(internalEvent, localSecret, loginId);
};

export const flush = () => {
    triggerManuallyAndFlush();
};
